// src/hooks/useGraphNodeValue.tsx
import { useDebugValue as useDebugValue3 } from "react";

// src/hooks/useGraphNodeValueBase.tsx
import {
  useMemoCondition
} from "@lyonph/react-hooks";
import { useDebugValue } from "react";
import { useStoreAdapter } from "react-store-adapter";
function useGraphNodeValueBase(context, node) {
  const store = useMemoCondition(() => context.get(node), { context, node }, (prev, next) => !(Object.is(prev.context, next.context) && Object.is(prev.node, next.node)));
  const value = useStoreAdapter(store);
  useDebugValue(value);
  return value;
}
var useGraphNodeValueBase_default = useGraphNodeValueBase;

// src/GraphDomainCore.tsx
import {
  useDebugValue as useDebugValue2,
  useEffect,
  useRef
} from "react";
import {
  get,
  createMemory,
  destroyMemory,
  subscribe
} from "graph-state";
import {
  useConstant
} from "@lyonph/react-hooks";
import {
  createNullaryModel,
  createValue,
  useScopedModelExists
} from "react-scoped-model";
import { createStoreAdapter } from "react-store-adapter";

// src/utils/OutOfGraphDomainError.tsx
var OutOfGraphDomainError = class extends Error {
  constructor() {
    super(`
  Attempt to access a graph domain instance externally.
  Please make sure that the <GraphDomain> is mounted and
  is an ancestor and the hook is being used inside a
  <GraphDomain>;
`);
  }
};

// src/GraphDomainCore.tsx
var GraphDomainCore = createNullaryModel(() => {
  const isMounted = useRef(true);
  const memory = useConstant(() => createMemory());
  const stores = useConstant(() => new Map());
  useDebugValue2(memory.nodes);
  useEffect(() => () => {
    isMounted.current = false;
    destroyMemory(memory);
  }, [memory]);
  return useConstant(() => ({
    memory,
    get: (node) => {
      const store = stores.get(node.key);
      if (store) {
        return store;
      }
      const newStore = createStoreAdapter({
        read: () => get(memory, node),
        subscribe: (callback) => subscribe(memory, node, callback),
        keepAlive: true
      });
      stores.set(node.key, newStore);
      return newStore;
    }
  }));
}, {
  displayName: "GraphDomainCore"
});
var useGraphDomainCore = createValue(GraphDomainCore);
function useGraphDomainRestriction() {
  const context = useScopedModelExists(GraphDomainCore);
  if (!context) {
    throw new OutOfGraphDomainError();
  }
}
var GraphDomainCore_default = GraphDomainCore;

// src/hooks/useGraphNodeValue.tsx
function useGraphNodeValue(node) {
  useGraphDomainRestriction();
  const current = useGraphNodeValueBase_default(useGraphDomainCore(), node);
  useDebugValue3(current);
  return current;
}
var useGraphNodeValue_default = useGraphNodeValue;

// src/hooks/useGraphNodeState.tsx
import { useDebugValue as useDebugValue4 } from "react";

// src/hooks/useGraphNodeDispatchBase.tsx
import {
  dispatch
} from "graph-state";
import {
  useCallbackCondition
} from "@lyonph/react-hooks";
function useGraphNodeDispatchBase(context, node) {
  return useCallbackCondition((action) => dispatch(context.memory, node, action), { context, node }, (prev, next) => !(Object.is(prev.context, next.context) && Object.is(prev.node, next.node)));
}

// src/hooks/useGraphNodeResetBase.tsx
import { reset } from "graph-state";
import {
  useCallbackCondition as useCallbackCondition2
} from "@lyonph/react-hooks";
function useGraphNodeResetBase(context, node) {
  return useCallbackCondition2(() => {
    reset(context.memory, node);
  }, { context, node }, (prev, next) => !(Object.is(prev.context, next.context) && Object.is(prev.node, next.node)));
}

// src/hooks/useGraphNodeState.tsx
function useGraphNodeState(node) {
  useGraphDomainRestriction();
  const context = useGraphDomainCore();
  const state = useGraphNodeValueBase_default(context, node);
  const dispatch2 = useGraphNodeDispatchBase(context, node);
  const reset2 = useGraphNodeResetBase(context, node);
  useDebugValue4([state, dispatch2, reset2]);
  return [state, dispatch2, reset2];
}

// src/hooks/useGraphNodeReset.tsx
import { useDebugValue as useDebugValue5 } from "react";
function useGraphNodeReset(node) {
  useGraphDomainRestriction();
  const reset2 = useGraphNodeResetBase(useGraphDomainCore(), node);
  useDebugValue5(reset2);
  return reset2;
}

// src/hooks/useGraphNodeMutate.tsx
import { useDebugValue as useDebugValue6 } from "react";

// src/hooks/useGraphNodeMutateBase.tsx
import { set } from "graph-state";
import {
  useCallbackCondition as useCallbackCondition3
} from "@lyonph/react-hooks";
function useGraphNodeMutateBase(context, node) {
  return useCallbackCondition3((action) => set(context.memory, node, action), { context, node }, (prev, next) => !(Object.is(prev.context, next.context) && Object.is(prev.node, next.node)));
}

// src/hooks/useGraphNodeMutate.tsx
function useGraphNodeMutate(node) {
  useGraphDomainRestriction();
  const dispatch2 = useGraphNodeMutateBase(useGraphDomainCore(), node);
  useDebugValue6(dispatch2);
  return dispatch2;
}

// src/hooks/useGraphNodeHydrate.tsx
import {
  hydrate
} from "graph-state";
function useGraphNodeHydrate(node, value) {
  useGraphDomainRestriction();
  const context = useGraphDomainCore();
  hydrate(context.memory, node, value);
}

// src/hooks/useGraphNodeDispatch.tsx
import { useDebugValue as useDebugValue7 } from "react";
function useGraphNodeDispatch(node) {
  const dispatch2 = useGraphNodeDispatchBase(useGraphDomainCore(), node);
  useDebugValue7(dispatch2);
  return dispatch2;
}

// src/hooks/useGraphNodeResource.tsx
import { useDebugValue as useDebugValue8 } from "react";
function useGraphNodeResource(node) {
  useGraphDomainRestriction();
  const value = useGraphNodeValue_default(node);
  useDebugValue8(value.status === "success" ? value.data : value);
  if (value.status === "success") {
    return value.data;
  }
  throw value.data;
}

// src/hooks/useGraphNodeSnapshotBase.tsx
import { useEffect as useEffect2 } from "react";
import {
  subscribe as subscribe2
} from "graph-state";
function useGraphNodeSnapshotBase(context, node, listener) {
  useEffect2(() => {
    let mounted = true;
    const internalListener = (value) => {
      if (mounted) {
        listener(value);
      }
    };
    const unsubscribe = subscribe2(context.memory, node, internalListener);
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [context, node, listener]);
}

// src/hooks/useGraphNodeSnapshot.tsx
function useGraphNodeSnapshot(node, listener) {
  useGraphDomainRestriction();
  useGraphNodeSnapshotBase(useGraphDomainCore(), node, listener);
}

// src/GraphDomain.tsx
import React from "react";
import { useScopedModelExists as useScopedModelExists2 } from "react-scoped-model";
import { StoreAdapterRoot } from "react-store-adapter";
var GraphDomain = ({ children }) => {
  const context = useScopedModelExists2(GraphDomainCore_default);
  if (context) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, children);
  }
  return /* @__PURE__ */ React.createElement(StoreAdapterRoot, null, /* @__PURE__ */ React.createElement(GraphDomainCore_default.Provider, null, children));
};
if (process.env.NODE_ENV !== "production") {
  GraphDomain.displayName = "GraphDomain";
}
var GraphDomain_default = GraphDomain;
export {
  GraphDomain_default as GraphDomain,
  useGraphNodeDispatch,
  useGraphNodeHydrate,
  useGraphNodeMutate,
  useGraphNodeReset,
  useGraphNodeResource,
  useGraphNodeSnapshot as useGraphNodeSelector,
  useGraphNodeSnapshot,
  useGraphNodeState,
  useGraphNodeValue_default as useGraphNodeValue
};
/**
 * @license
 * MIT License
 *
 * Copyright (c) 2020 Alexis Munsayac
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 *
 * @author Alexis Munsayac <alexis.munsayac@gmail.com>
 * @copyright Alexis Munsayac 2020
 */
//# sourceMappingURL=index.js.map
